const USER_INTENT_MS = 400;
const INTENT_EVENTS = ['wheel', 'touchmove', 'keydown'] as const;

/**
 * Keeps a scrolling pane glued to its bottom while its content grows (late
 * image decode, new rows, a shrinking viewport), until the user scrolls away.
 *
 * Pinned state changes only on scroll events that follow real user input.
 * Scroll events the browser fires by itself on layout shifts are ignored, so
 * they cannot unpin the pane in the middle of a load.
 */
export class StickToBottom {
  private isPinned = true;
  private userIntent = false;
  private holdingPointer = false;
  private intentTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly observer: ResizeObserver | null;
  private readonly cleanups: (() => void)[] = [];

  constructor(
    private readonly pane: HTMLElement,
    observe: readonly Element[] = [],
    private readonly threshold = 120,
  ) {
    for (const name of INTENT_EVENTS) this.listen(pane, name, () => this.noteIntent());
    this.listen(pane, 'pointerdown', () => this.holdPointer());
    this.listen(pane, 'scroll', () => this.onScroll());
    this.observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.settle());
    for (const element of new Set([pane, ...observe])) this.observer?.observe(element);
  }

  get pinned(): boolean {
    return this.isPinned;
  }

  /** Pins and jumps to the bottom (a new conversation, or the user's own send). */
  pin(): void {
    this.isPinned = true;
    this.scrollToBottom();
  }

  /** Stops following the bottom, for example while older rows are inserted above. */
  release(): void {
    this.isPinned = false;
  }

  scrollToBottom(): void {
    this.pane.scrollTop = this.pane.scrollHeight;
  }

  dispose(): void {
    clearTimeout(this.intentTimer);
    this.observer?.disconnect();
    for (const cleanup of this.cleanups.splice(0)) cleanup();
  }

  private settle(): void {
    if (this.isPinned) this.scrollToBottom();
  }

  private onScroll(): void {
    if (!this.userIntent && !this.holdingPointer) return;
    const distance = this.pane.scrollHeight - this.pane.clientHeight - this.pane.scrollTop;
    this.isPinned = distance < this.threshold;
  }

  private noteIntent(): void {
    this.userIntent = true;
    clearTimeout(this.intentTimer);
    this.intentTimer = setTimeout(() => (this.userIntent = false), USER_INTENT_MS);
  }

  private holdPointer(): void {
    this.holdingPointer = true;
    this.noteIntent();
    const release = (): void => {
      this.holdingPointer = false;
      this.noteIntent();
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
    };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    this.cleanups.push(() => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
    });
  }

  private listen<K extends keyof HTMLElementEventMap>(
    target: HTMLElement,
    name: K,
    handler: () => void,
  ): void {
    target.addEventListener(name, handler, { passive: true });
    this.cleanups.push(() => target.removeEventListener(name, handler));
  }
}
